import React, {Fragment, ReactNode} from 'react'
import styles from './UIGameFeedMessage.module.scss'
import {t} from '../../../common/T'
import {FeedMessageData} from './FeedMessageData'
// eslint-disable-next-line no-restricted-imports
import {UIUtilsChat} from '../../../../../../code/client/shared/scripts/ui_chat/UIUtilsChat'
import {Avatar} from '../../../common/Avatar/Avatar'
import clsx from 'clsx'
// eslint-disable-next-line no-restricted-imports
import {GameColor} from '../../../../../../code/client/shared/scripts/GameColor'
// eslint-disable-next-line no-restricted-imports
import {showRulebookPopup} from '../../../../../../code/client/shared/scripts/ui_rulebook/UIRulebookPopupGlobal'
import {UIGamePlayerOptionsDropdown} from '../../../common/UIGamePlayerOptionsDropdown'
import {CARD_TOOLTIP_LINK_PREFIX} from './FeedMessageDataLogs'
import {CardEnum} from '@colonist/game-states'
import {CardDataController} from '@colonist/game-card-types'
import {Tooltip} from '../../../common/Tooltip'
import {useUIGamePlayersContext} from '../../../contexts/UIGamePlayersContext'
import {useUIGameUserStatesContext} from '../../../contexts/UIGameUserStatesContext'
import {useUIGamePauseContext} from '../../../core/UIGamePause/UIGamePauseContext'

const replaceDevCardLinksWithTooltipComponent = (str: string) => {
    const linkRegex = /<a href='(.*)'>(.*)<\/a>/gi
    const [match] = Array.from(str.matchAll(linkRegex))
    if(match === undefined) return [str]

    const [href, text] = match.slice(1)
    const devCardRegex = new RegExp(`${CARD_TOOLTIP_LINK_PREFIX}([0-9]*)`, 'gi')
    const [devCardMatch] = Array.from(href.matchAll(devCardRegex))
    if(devCardMatch === undefined) return [str]

    const cardEnum = Number(devCardMatch[1]) as CardEnum
    const popupBody = CardDataController.getCardDataForCard(cardEnum)?.popupBody
    if(popupBody == undefined) return [str]

    const translatedPopupBody = t(popupBody.key, popupBody.options)
    const replacement = <Tooltip className={styles.messageWithTooltip} content={translatedPopupBody}>
        <span dangerouslySetInnerHTML={{__html: text}}></span>
    </Tooltip>

    const messageWithReplacement = str.replace(linkRegex, '{{replacement}}')
    const messageParts = messageWithReplacement.split(/{{replacement}}/g)

    const messagePartsWithTooltip: (ReactNode | string)[] = [
        ...(messageParts[0] != null ? [messageParts[0]] : []),
        replacement,
        ...(messageParts[1] != null ? [messageParts[1]] : []),
    ]
    return messagePartsWithTooltip
}

// TODO: Stop using HTML and convert all the replacement to React components
function formatMessageHTML(message: string, colorAndBoldUsernames?: boolean) {
    if(colorAndBoldUsernames) message = UIUtilsChat.colorAndBoldUsernames(message)
    message = UIUtilsChat.replaceKeywordsWithImages(message)
    const htmlMessageWithReactComponent = replaceDevCardLinksWithTooltipComponent(message)

    return htmlMessageWithReactComponent
}

interface UIGameFeedMessageCoreProps {
    opacity?: number
    onClick?: undefined | ((logIndex: number) => void)
    logIndex?: number
}

type UIGameFeedMessageProps = {
    message?: never
    children: React.ReactNode
} & UIGameFeedMessageCoreProps | {
    message: FeedMessageData
    children?: never
} & UIGameFeedMessageCoreProps

export function UIGameFeedMessage({
    message,
    children,
    opacity,
    onClick,
    logIndex,
}: UIGameFeedMessageProps) {
    const {hasOnlyOneHumanPlayer} = useUIGameUserStatesContext()
    const {getPlayerValidator} = useUIGamePlayersContext()
    const {requestPause} = useUIGamePauseContext()

    if(message === undefined) {
        return <div className={styles.feedMessage}>{children}</div>
    }

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement
        const link = target.closest('a')

        if(link && link.href) {
            event.preventDefault()
            handleLink(link.href)
            return
        }

        onClick?.(logIndex ?? 0)
    }

    const handleLink = (href: string) => {
        const url = new URL(href)

        if(url.hash === UIUtilsChat.OPEN_RULEBOOK_LINK_HASH) {
            if(hasOnlyOneHumanPlayer) requestPause()
            showRulebookPopup()
        }
    }

    const {
        text,
        icon,
        username,
        usernameColor,
        colorAndBoldUsernames,
        italics,
        isCentered,
        profilePictureUrl,
    } = message

    const translated = t(text)
    const messageParts = formatMessageHTML(translated, colorAndBoldUsernames)
    const usernameColorHex = usernameColor !== undefined ? GameColor.getPlayerBGDarkHash(usernameColor) : '#000'

    const renderUsername = () => {
        const defaultUsername = <span className={styles.username} style={{color: usernameColorHex}}>{username}</span>

        if(usernameColor == undefined) {
            return defaultUsername
        }

        const playerValidator = getPlayerValidator(usernameColor)

        return <UIGamePlayerOptionsDropdown className={styles.playerDropdown} playerValidator={playerValidator}>
            <span className={styles.username} style={{color: usernameColorHex}}>{username}</span>
        </UIGamePlayerOptionsDropdown>
    }

    return (
        <div
            className={clsx(styles.feedMessage, italics && styles.italics, isCentered && styles.centered)}
            onClick={handleClick}
            style={{opacity: opacity}}>
            {((icon != null) || profilePictureUrl) &&
                <Avatar
                    className={styles.avatar}
                    hideBackground={!profilePictureUrl}
                    image={icon}
                    profilePictureUrl={profilePictureUrl}
                />}
            {username !== undefined &&
                <>
                    {renderUsername()}
                    {': '}
                </>}
            {messageParts.map((part, index) => {
                if(typeof part === 'string') {
                    return <span dangerouslySetInnerHTML={{__html: part}} key={index}/>
                } else {
                    return <Fragment key={index}>{part}</Fragment>
                }
            })}
        </div>
    )
}
